/**
 * The two constants the silent-renewal handshake is built on.
 *
 * They live in a module of their OWN, with no imports, because both ends of the
 * handshake load them and the two ends run in different documents:
 *
 *   - the PARENT (`auth/silentAuthorize.ts`) opens the hidden frame and listens;
 *   - the FRAME (`auth/silentCallbackBridge.ts`) runs inside BeHeard's own
 *     redirect URI, before React mounts, and posts the authorize answer up.
 *
 * The bridge must stay dependency-free: it runs on the very first line of
 * `main.tsx`, and pulling the store / config / RTK Query graph in just to read a
 * string would make the frame boot the whole application to say one sentence.
 */

/**
 * Prefix on the OAuth `state` of a SILENT authorize attempt.
 *
 * This is the only thing that distinguishes a silent callback from an ordinary
 * interactive login callback, and the distinction is load-bearing twice over:
 *
 *   - the frame uses it to decide "this is a silent attempt — post the answer to
 *     my parent and do NOT boot the SPA", so a real login callback that happens
 *     to be framed is still handled normally;
 *   - the parent uses it (plus the full random suffix) to ignore an answer from
 *     an earlier, abandoned attempt.
 */
export const SILENT_STATE_PREFIX = 'beheard-silent.';

/** Discriminator on the postMessage payload, so no other message is mistaken for ours. */
export const SILENT_MESSAGE_SOURCE = 'beheard.silent-authorize';

/** What the frame posts up: the query parameters Central redirected back with. */
export interface SilentCallbackMessage {
  source: typeof SILENT_MESSAGE_SOURCE;
  /** The `state` the attempt was started with, echoed by Central. */
  state: string;
  /** `code`, or `error` / `error_description` — whichever Central sent. */
  params: Record<string, string>;
}

/** The authorize-response parameters the handshake carries. Nothing else is forwarded. */
export const SILENT_FORWARDED_PARAMS: readonly string[] = [
  'code',
  'error',
  'error_description',
  'error_uri',
];
